import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { HeaderBaseComponent, Instance, OverlayService } from '@finra/rampdi-forms-common';
import { Observable, timer } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DisplayRulesService, FILING_STATUS, FilingStatusDisplayRules } from '../../services/display-rules.service';
import { InstanceService } from '../../services/instance.service';
import { ExternalService } from '@finra/angular-data-collection-sdk';
import { environment, Versions } from 'projects/app-mu3-form/src/environments/environment';
import { WindowService } from '../../services/window.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent extends HeaderBaseComponent implements OnInit {
  data$: Observable<Instance<any>>;
  showHeader$;
  private _DRS = inject(DisplayRulesService);
  displayRules$: Observable<FilingStatusDisplayRules> = this._DRS.displayRules$;
  successfulDeletion = 'The delete request is being processed. This may take few seconds.';
  successfulRecall = 'MU3 Form recalled successfully.';
  isUnsubmitted = false;
  @Input() totalCharges = 0;
  @Output() openInvoicePopup = new EventEmitter();
  @Input() userName: string;
  @Input() subjectId: string;
  @Input() isFormView: boolean;

  constructor(
    private router: Router,
    private instanceService: InstanceService,
    private overlayService: OverlayService,
    private es: ExternalService,
    private windowService: WindowService,
  ) {
    super(router);
  }

  ngOnInit() {
    this.data$ = this.getInstanceData();
  }

  retrieveTemplateInfoFromCurrentInstance(): {
    templateVersion;
    templateName;
    templateNamespace;
    id;
    instanceMetadata;
    currentEnvVersion;
  } {
    const { templateVersion, templateName, templateNamespace, id, instanceMetadata } =
      this.instanceService.currentInstance;
    return {
      templateVersion,
      templateName,
      templateNamespace,
      id,
      instanceMetadata,
      currentEnvVersion: environment.versions[(templateVersion ?? environment.currentVersion) as Versions],
    };
  }

  deleteForm() {
    const { templateVersion, templateName, templateNamespace, id, currentEnvVersion } =
      this.retrieveTemplateInfoFromCurrentInstance();
    this.overlayService
      .openModal(`Confirm Delete`, `warning`, `Are you sure you want to delete this MU3?`, [
        { name: 'Cancel', value: false, theme: 'secondary', 'aria-label': 'Cancel MU3 deletion' },
        { name: 'Delete', value: true, theme: 'primary', 'aria-label': 'Confirm MU3 deletion' },
      ])
      .afterClosed$.toPromise()
      .then((response) => {
        if (response.data) {
          globalThis.finformUnsavedChanges = false;
          this.es
            .deleteInstanceUsingDELETE(templateNamespace, templateName, templateVersion, id)
            .pipe(take(1))
            .subscribe((res) => {
              this.instanceService.handleSuccessResponse(this.successfulDeletion);
              if (this._DRS.userPermissions.isIndividualUser) {
                timer(5000)
                  .pipe(take(1))
                  .subscribe(() => {
                    window.open(currentEnvVersion.urls.individualProfile, '_self');
                  });
              } else {
                timer(5000)
                  .pipe(take(1))
                  .subscribe(() => {
                    window.open(currentEnvVersion.urls.workManagement, '_self');
                  });
              }
            });
        }
      });
  }

  recallForm() {
    this.overlayService
      .openModal(`Warning`, `warning`, `Are you sure you want to recall the attestation request for this record?`, [
        { name: 'Cancel', value: false, theme: 'secondary', 'aria-label': 'Cancel attestation recall' },
        { name: 'Recall', value: true, theme: 'primary', 'aria-label': 'Confirm attestation recall' },
      ])
      .afterClosed$.toPromise()
      .then((response) => {
        if (response.data) {
          globalThis.finformUnsavedChanges = false;
          this.instanceService.recallFiling().subscribe(
            (res) => {
              this.isUnsubmitted = res.instanceMetadata['attributes']?.filingStatus.current === 'Unsubmitted';
              this.instanceService.handleSuccessResponse(this.successfulRecall);
              this.windowReload();
            },
            (err) => {
              this.instanceService.handleErrorResponse(err);
            },
          );
        }
      });
  }

  windowReload() {
    this.windowService.location.reload();
  }

  openInvoice() {
    this.openInvoicePopup.emit();
  }

  get canViewInvoice() {
    const isCompany = this._DRS.userPermissions?.isCompanyUser;

    const subjectId = this.instanceService.currentInstance?.instanceMetadata?.target?.['subjectId'];
    const entityId = this.instanceService.currentInstance?.instanceMetadata?.target?.['entityId'];

    return entityId === subjectId || isCompany;
  }

  get isFilingProcessed() {
    const filingStatus = this.instanceService.currentInstance?.instanceMetadata?.attributes?.filingStatus?.current;
    return filingStatus === FILING_STATUS.PROCESSED;
  }

  get isCurrentVersion() {
    return this.instanceService.currentInstance?.templateVersion === environment.currentVersion;
  }
}
